import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { useT, useDocumentTitleKey } from "@/i18n/locale-context";

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Connexion — Glow Up Coach" }] }),
  component: LoginPage,
});

function LoginPage() {
  useDocumentTitleKey("login.head.title");
  const t = useT();
  const nav = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) nav({ to: "/app" });
    });
  }, [nav]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/app" },
        });
        if (error) throw error;
        toast.success(t("login.toast.check_email"));
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        nav({ to: "/app" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("login.toast.generic_error"));
    } finally {
      setLoading(false);
    }
  }

  async function onOAuth(provider: "google" | "apple") {
    setLoading(true);
    try {
      const res = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin + "/app",
      });
      if (res.error) throw res.error;
      if (!res.redirected) nav({ to: "/app" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("login.toast.oauth_error", provider));
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        <h1 className="font-display font-bold text-3xl mb-2 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {mode === "signin" ? t("login.title.signin") : t("login.title.signup")}
        </h1>
        <p className="text-muted-foreground text-sm mb-8">
          {mode === "signin" ? t("login.subtitle.signin") : t("login.subtitle.signup")}
        </p>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => onOAuth("google")}
            disabled={loading}
            className="w-full border border-foreground/15 rounded-xl py-3 text-sm font-semibold hover:bg-foreground/5 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {t("login.oauth.google")}
          </button>
          <button
            onClick={() => onOAuth("apple")}
            disabled={loading}
            className="w-full bg-foreground text-background rounded-xl py-3 text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 12.5c-.02-2.06 1.68-3.05 1.76-3.1-.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.65 0-1.65-.73-2.71-.71-1.39.02-2.68.81-3.4 2.06-1.45 2.52-.37 6.25 1.05 8.3.69 1 1.51 2.13 2.58 2.09 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.6.67 2.7.65 1.12-.02 1.82-1.02 2.5-2.03.79-1.17 1.12-2.3 1.13-2.36-.02-.01-2.17-.83-2.2-3.36zM14.97 6.27c.57-.7.96-1.66.86-2.62-.83.03-1.84.55-2.44 1.24-.53.61-1.01 1.6-.88 2.54.93.07 1.88-.47 2.46-1.16z"/>
            </svg>
            {t("login.oauth.apple")}
          </button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-foreground/10" />
          <span className="text-xs text-muted-foreground uppercase tracking-widest">{t("login.divider.or")}</span>
          <div className="flex-1 h-px bg-foreground/10" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="email"
            required
            placeholder={t("login.input.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-foreground/15 bg-background text-sm focus:outline-none focus:border-accent"
          />
          <input
            type="password"
            required
            minLength={8}
            placeholder={t("login.input.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-foreground/15 bg-background text-sm focus:outline-none focus:border-accent"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-foreground text-background py-3 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "…" : mode === "signin" ? t("login.btn.signin") : t("login.btn.signup")}
          </button>
        </form>

        {mode === "signin" && (
          <Link
            to="/forgot-password"
            className="block text-center text-xs text-muted-foreground hover:text-foreground mt-4"
          >
            {t("login.forgot_password")}
          </Link>
        )}

        <button
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          className="block w-full text-center text-sm text-muted-foreground hover:text-foreground mt-6"
        >
          {mode === "signin" ? t("login.toggle.to_signup") : t("login.toggle.to_signin")}
        </button>

        <Link to="/" className="block text-center text-xs text-muted-foreground hover:text-foreground mt-8">
          {t("login.back_home")}
        </Link>
      </div>
    </div>
  );
}
